import {
  BadgeCheck,
  Calculator,
  UserCheck,
  HelpCircle,
} from "lucide-react";

/**
 * Evidence class visual language — Fursa Design · Phase 1.
 *
 * The four evidence classes surface at the granular field level (see the
 * Sprint-6 pipeline data model). Every class must communicate through
 * label + icon FIRST, tone SECOND — so the semantic remains readable
 * without color (WCAG-friendly, colour-blind-safe).
 *
 *   Verified            — cited, sourced (emerald · positive signal)
 *   Estimated           — derived by methodology + assumptions (navy · neutral)
 *   Expert Assessment   — professional judgement (gold · rare accent)
 *   Unknown             — explicitly missing, never an error (ink · neutral)
 */

export const EVIDENCE_CLASSES = {
  verified: {
    key: "verified",
    label: "موثّق",
    hint:  "مدعوم بمصدر",
    icon:  BadgeCheck,
    tone:  "border-fursa-200 bg-fursa-50 text-fursa-700",
    iconTone: "text-fursa-600",
  },
  estimated: {
    key: "estimated",
    label: "تقديري",
    hint:  "منهجية + افتراضات",
    icon:  Calculator,
    tone:  "border-intel-200 bg-intel-50 text-intel-800",
    iconTone: "text-intel-700",
  },
  expert_assessment: {
    key: "expert_assessment",
    label: "رأي مهني",
    hint:  "تقدير خبير",
    icon:  UserCheck,
    tone:  "border-gold-200 bg-gold-50 text-gold-700",
    iconTone: "text-gold-600",
  },
  unknown: {
    key: "unknown",
    label: "غير متوفّر",
    hint:  "معلومة ناقصة",
    icon:  HelpCircle,
    tone:  "border-surface-300 bg-surface-50 text-ink-700",
    iconTone: "text-ink-500",
  },
};

/**
 * <EvidenceBadge class="verified" /> — compact chip form.
 *
 * Props:
 *   • evidenceClass  — one of verified | estimated | expert_assessment | unknown
 *   • size           — "sm" (default) | "md"
 *   • showHint       — surface the secondary label (defaults false)
 *   • className      — extra tailwind classes
 */
export const EvidenceBadge = ({
  evidenceClass,
  size = "sm",
  showHint = false,
  className = "",
}) => {
  const meta = EVIDENCE_CLASSES[evidenceClass] ?? EVIDENCE_CLASSES.unknown;
  const Icon = meta.icon;
  const paddings = size === "md"
    ? "px-2.5 py-1 text-xs"
    : "px-2 py-0.5 text-[11px]";

  return (
    <span
      role="status"
      aria-label={`${meta.label}${meta.hint ? " — " + meta.hint : ""}`}
      data-testid={`evidence-${meta.key}`}
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${meta.tone} ${paddings} ${className}`}
    >
      <Icon className={`h-3 w-3 ${meta.iconTone}`} strokeWidth={2} aria-hidden />
      <span>{meta.label}</span>
      {showHint && (
        <span className="hidden text-[10px] font-normal opacity-70 sm:inline">
          · {meta.hint}
        </span>
      )}
    </span>
  );
};

/**
 * <EvidenceComposition composition={{verified:3, estimated:2, ...}} /> — the
 * horizontal composition bar. Shows how many claims fall in each class.
 * Renders "N × verified · N × estimated · ..." with icons.
 */
export const EvidenceComposition = ({ composition, className = "" }) => {
  if (!composition) return null;
  const total = Object.values(composition).reduce((a, b) => a + (b || 0), 0);
  if (total === 0) return null;

  const rows = Object.entries(EVIDENCE_CLASSES).map(([key, meta]) => ({
    ...meta,
    count: composition[key] ?? 0,
  }));

  return (
    <div
      data-testid="evidence-composition"
      role="list"
      aria-label="تركيبة الأدلّة"
      className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs ${className}`}
    >
      {rows
        .filter((r) => r.count > 0)
        .map(({ key, label, icon: Icon, iconTone, count }) => (
          <span
            role="listitem"
            key={key}
            data-testid={`evidence-count-${key}`}
            className="inline-flex items-center gap-1.5 text-ink-700"
          >
            <Icon className={`h-3.5 w-3.5 ${iconTone}`} strokeWidth={1.75} aria-hidden />
            <span dir="ltr" className="num font-mono font-semibold text-ink-900">
              {count}
            </span>
            <span className="text-ink-500">{label}</span>
          </span>
        ))}
    </div>
  );
};
